import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Edit, Close, ContentCopy, Lock, FactCheckOutlined, Upgrade, Visibility, History } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination from "../components/Pagination/StandardPagination";
import SavingOverlay from "../components/SavingOverlay";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation, useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography, pageHeader } from "../theme/designSystem";
import {
  assessmentFormsService,
  assessmentErrorMessage,
  AssessmentFormRow,
} from "../services/assessmentFormsService";

const DEFAULT_PAGE_SIZE = 10;

// Cadastro de formulários de avaliação (F1 das Avaliações — Somente Administrador).
// Formulário já aplicado a paciente é imutável na estrutura (cadeado): duplicar para evoluir.
const AssessmentForms: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { navigateTo } = useRouter();
  const { toast, showToast, hideToast } = useToast();

  const [rows, setRows] = useState<AssessmentFormRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"active" | "inactive" | "all">("active");

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("active");
  };

  const hasFilterChanges = search !== "" || statusFilter !== "active";

  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [duplicateTarget, setDuplicateTarget] = useState<AssessmentFormRow | null>(null);
  const [duplicateTitle, setDuplicateTitle] = useState("");
  const [versionTarget, setVersionTarget] = useState<AssessmentFormRow | null>(null);
  const [versionTitle, setVersionTitle] = useState("");

  const load = useCallback(async (
    page: number, term: string, status: "active" | "inactive" | "all", size: number) => {
    setLoading(true);
    try {
      const result = await assessmentFormsService.list({
        search: term.trim() || undefined,
        status,
        pageNumber: page,
        pageSize: size,
      });
      setRows(result.items);
      setTotalCount(result.totalCount);
    } catch (e) {
      showToast(assessmentErrorMessage(e, "Erro ao carregar os formulários."), "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageNumber(1);
      load(1, search, statusFilter, pageSize);
    }, search ? 300 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, pageSize]);

  const changePage = (page: number) => {
    setPageNumber(page);
    load(page, search, statusFilter, pageSize);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const toggleActive = async (row: AssessmentFormRow) => {
    try {
      await assessmentFormsService.update(row.id, {
        title: row.title,
        description: row.description,
        allowPartialCompletion: row.allowPartialCompletion,
        calcTypeId: row.calcTypeId,
        active: !row.active,
      });
      await load(pageNumber, search, statusFilter, pageSize);
    } catch (e) {
      showToast(assessmentErrorMessage(e, "Erro ao alterar o formulário."), "error");
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const id = await assessmentFormsService.create({
        title: newTitle,
        description: newDescription.trim() || null,
        allowPartialCompletion: false,
        calcTypeId: null,
      });
      showToast("Formulário criado.", "success");
      setCreateOpen(false);
      navigateTo("assessment-form-editor", { formId: id });
    } catch (e) {
      showToast(assessmentErrorMessage(e, "Erro ao criar o formulário."), "error");
    } finally {
      setSaving(false);
    }
  };

  const openNewVersion = (row: AssessmentFormRow) => {
    const baseTitle = row.title.replace(/\s*—\s*v\d+$/u, "");
    setVersionTitle(`${baseTitle} — v${row.version + 1}`);
    setVersionTarget(row);
  };

  const handleNewVersion = async () => {
    if (!versionTarget) return;
    setSaving(true);
    try {
      const id = await assessmentFormsService.newVersion(versionTarget.id, versionTitle);
      showToast("Nova versão criada (inativa). Revise e ative quando quiser colocá-la no ar.", "success");
      setVersionTarget(null);
      navigateTo("assessment-form-family", { formId: id });
    } catch (e) {
      showToast(assessmentErrorMessage(e, "Erro ao criar a nova versão."), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateTarget) return;
    setSaving(true);
    try {
      const id = await assessmentFormsService.duplicate(duplicateTarget.id, duplicateTitle);
      showToast("Formulário duplicado.", "success");
      setDuplicateTarget(null);
      navigateTo("assessment-form-editor", { formId: id });
    } catch (e) {
      showToast(assessmentErrorMessage(e, "Erro ao duplicar o formulário."), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Formulários de avaliação
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">Instrumentos aplicados nas avaliações dos pacientes: questões organizadas por categorias e escalas de resposta.
                Formulário já aplicado a um paciente tem a estrutura travada — duplique para evoluir.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
              <TextField
                size="small"
                label="Busca"
                placeholder="Título ou descrição"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 255 }}
                sx={{ minWidth: 220, backgroundColor: "#fff" }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel shrink>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as "active" | "inactive" | "all")} notched>
                  <MenuItem value="active">Ativos</MenuItem>
                  <MenuItem value="inactive">Inativos</MenuItem>
                  <MenuItem value="all">Todos</MenuItem>
                </Select>
              </FormControl>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={() => { setNewTitle(""); setNewDescription(""); setCreateOpen(true); }} title="Novo formulário" />
              </div>
            </div>

            <ResultCount shown={rows.length} total={totalCount} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
              <FactCheckOutlined sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>Nenhum formulário no filtro selecionado</Typography>
              <Typography variant="body2">Ajuste a busca/status ou cadastre um novo formulário.</Typography>
            </Box>
          ) : (
            <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
              {/* Cabeçalho: toda lista tem um, e cada dado fica na sua coluna */}
              <Box className="c4u-table__header">
                <Box className="admin-plans-header-cell" sx={{ flex: "1 1 240px", minWidth: 0 }}>Instrumento</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px" }}>Situação</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 190px" }}>Ações</Box>
              </Box>

              {rows.map((row) => (
                <Box
                  key={row.id}
                  className="c4u-table__row"
                  sx={{ opacity: row.active ? 1 : 0.65 }}
                >
                  <Box className="admin-plans-cell" data-label="Instrumento" sx={{ flex: "1 1 240px", minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 600, color: colors.textPrimary, wordBreak: "break-word", lineHeight: 1.25 }}>
                        {row.title}
                      </Typography>
                      <Chip label={`v${row.version}`} size="small" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, backgroundColor: "#e9ecef", color: colors.textSecondary }} />
                      {row.versionsCount > 1 && (
                        <Tooltip title="Ver o histórico de versões deste instrumento" arrow>
                          <Chip
                            label={`${row.versionsCount} versões`}
                            size="small"
                            onClick={() => navigateTo("assessment-form-family", { formId: row.id })}
                            sx={{ height: 18, fontSize: "0.65rem", cursor: "pointer", backgroundColor: "#e0f5f7", color: colors.primary, fontWeight: 600 }}
                          />
                        </Tooltip>
                      )}
                      {row.locked && (
                        <Tooltip title={`Aplicado ${row.applicationsCount}x a pacientes: estrutura travada (duplique para evoluir)`} arrow>
                          <Lock sx={{ fontSize: "1rem", color: "#d98c00" }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block" }}>
                      {row.description || "Sem descrição"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                      {row.sectionsCount} seç{row.sectionsCount === 1 ? "ão" : "ões"} · {row.questionsCount} item{row.questionsCount === 1 ? "" : "s"} · {row.applicationsCount} aplicaç{row.applicationsCount === 1 ? "ão" : "ões"}
                      {row.calcTypeName ? ` · Resultado: ${row.calcTypeName}` : ""}
                    </Typography>
                  </Box>

                  <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: "0 0 90px" }}>
                    <Chip
                      label={row.active ? "Ativo" : "Inativo"}
                      size="small"
                      onClick={(e) => { e.stopPropagation(); toggleActive(row); }}
                      title={row.active ? "Clique para inativar" : "Clique para ativar"}
                      sx={{ color: "white", fontWeight: 600, cursor: "pointer", backgroundColor: row.active ? "#28a745" : "#adb5bd" }}
                    />
                  </Box>
                  <Box className="admin-plans-cell admin-plans-actions" data-label="Ações" sx={{ flex: "0 0 190px", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                      <Tooltip title="Versões deste instrumento" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); navigateTo("assessment-form-family", { formId: row.id }); }}
                          className="c4u-row-action"
                          sx={{ backgroundColor: "#6c757d", "&:hover": { backgroundColor: "#5a6268" } }}
                        >
                          <History sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Pré-visualizar: ver o questionário como o profissional verá (nada é gravado)" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); window.open(`${window.location.origin}/?page=assessment-application&formId=${row.id}`, "_blank"); }}
                          className="c4u-row-action"
                          sx={{ backgroundColor: "#17a2b8", "&:hover": { backgroundColor: "#138496" } }}
                        >
                          <Visibility sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Nova versão: cópia na mesma família, que nasce inativa" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); openNewVersion(row); }}
                          className="c4u-row-action"
                          sx={{ backgroundColor: "#28a745", "&:hover": { backgroundColor: "#218838" } }}
                        >
                          <Upgrade sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicar (formulário novo e independente)" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setDuplicateTarget(row); setDuplicateTitle(`${row.title} (cópia)`); }}
                          className="c4u-row-action"
                          sx={{ backgroundColor: "#6610f2", "&:hover": { backgroundColor: "#520dc2" } }}
                        >
                          <ContentCopy sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Abrir editor" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); navigateTo("assessment-form-editor", { formId: row.id }); }}
                          className="c4u-row-action"
                          sx={{ backgroundColor: "#03B4C6", "&:hover": { backgroundColor: "#029AAB" } }}
                        >
                          <Edit sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {!loading && totalCount > 0 && (
            <StandardPagination
              currentPage={pageNumber}
              totalPages={totalPages}
              itemsPerPage={pageSize}
              totalItems={totalCount}
              onPageChange={changePage}
              onItemsPerPageChange={(n) => { setPageSize(n); setPageNumber(1); }}
            />
          )}
          </div>
        </Container>
      </Box>

      {/* Novo formulário */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            Novo formulário de avaliação
          </Typography>
          <IconButton onClick={() => setCreateOpen(false)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Título *"
            size="small"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 255 }}
            fullWidth
          />
          <TextField
            label="Descrição"
            size="small"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 1000 }}
            multiline
            minRows={2}
            fullWidth
          />
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            Seções, itens, opções de resposta e o tipo de resultado são configurados no editor, na próxima etapa.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setCreateOpen(false)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button variant="contained" disabled={!newTitle.trim() || saving} onClick={handleCreate} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            Criar e abrir editor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicar */}
      <Dialog open={duplicateTarget !== null} onClose={() => setDuplicateTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            Duplicar formulário
          </Typography>
          <IconButton onClick={() => setDuplicateTarget(null)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Copia toda a estrutura de <strong>{duplicateTarget?.title}</strong> — categorias, questões e escalas — para um formulário novo e editável. As aplicações já registradas continuam no formulário original.
          </Typography>
          <TextField
            label="Título do novo formulário *"
            size="small"
            value={duplicateTitle}
            onChange={(e) => setDuplicateTitle(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 255 }}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setDuplicateTarget(null)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button variant="contained" disabled={!duplicateTitle.trim() || saving} onClick={handleDuplicate} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            Duplicar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nova versão (mesma família) */}
      <Dialog open={versionTarget !== null} onClose={() => setVersionTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            Nova versão do formulário
          </Typography>
          <IconButton onClick={() => setVersionTarget(null)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Cria a <strong>v{(versionTarget?.version ?? 1) + 1}</strong> de <strong>{versionTarget?.title}</strong> na mesma família: toda a estrutura é copiada para uma versão nova e editável, e o sistema guarda o vínculo entre as versões (base da comparação evolutiva). As aplicações já registradas continuam presas à versão em que foram feitas.
          </Typography>
          <TextField
            label="Título da nova versão *"
            size="small"
            value={versionTitle}
            onChange={(e) => setVersionTitle(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 255 }}
            fullWidth
          />
          <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 1 }}>
            A nova versão nasce <strong>inativa</strong>, para você revisar antes de colocá-la em uso.
            Como só uma versão fica ativa por vez, para ativá-la é preciso inativar a anterior.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setVersionTarget(null)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button variant="contained" disabled={!versionTitle.trim() || saving} onClick={handleNewVersion} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            Criar nova versão
          </Button>
        </DialogActions>
      </Dialog>

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default AssessmentForms;
